import { useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { documents } from "@/lib/endpoints";
import type { DocumentResponse } from "@/lib/api-types";
import { cn } from "@/lib/utils";

/**
 * StepDocuments — optional supporting documents upload.
 *
 * N-400 applicants must mail supporting documents with their application.
 * This step lets them upload digital copies to keep them organized.
 * All documents are encrypted at rest (server-side envelope encryption).
 *
 * Skippable — not required to proceed to checkout.
 */

// Documents USCIS typically requires with N-400
const REQUIRED_DOCS = [
  {
    type: "green_card",
    label: "Permanent Resident Card",
    hint: "Both sides of your Green Card (Form I-551)",
    required: true,
  },
  {
    type: "passport_photo",
    label: "Passport-style photos",
    hint: "Two identical color photos, 2×2 inches",
    required: true,
  },
  {
    type: "marriage_certificate",
    label: "Marriage certificate",
    hint: "Required if applying based on marriage to a U.S. citizen",
    required: false,
  },
  {
    type: "divorce_decree",
    label: "Divorce decree(s)",
    hint: "For any prior marriages (applicant or spouse)",
    required: false,
  },
  {
    type: "name_change",
    label: "Name change document",
    hint: "Court order or marriage/divorce certificate showing name change",
    required: false,
  },
  {
    type: "other",
    label: "Other supporting document",
    hint: "Any additional document relevant to your application",
    required: false,
  },
];

export function StepDocuments() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const docsQ = useQuery({
    queryKey: ["case", id, "documents"],
    queryFn: () => documents.list(id!),
    enabled: !!id,
  });

  const uploadMut = useMutation({
    mutationFn: ({ file, docType }: { file: File; docType: string }) =>
      documents.upload(id!, file, docType),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["case", id, "documents"] }),
  });

  const deleteMut = useMutation({
    mutationFn: (docId: string) => documents.delete(id!, docId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["case", id, "documents"] }),
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold font-display text-slate-900">Supporting documents</h2>
        <p className="text-slate-500 mt-1 text-sm leading-relaxed">
          Upload digital copies of your supporting documents. These are mailed with your N-400.
          You can skip this step and add documents later.
        </p>
      </div>

      {/* Info callout */}
      <div className="flex gap-3 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
        <svg className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p className="text-xs text-blue-800 leading-relaxed">
          Documents are encrypted at rest and only accessible to you.
          USCIS requires <strong>paper copies</strong> mailed with your application — uploads here are for your reference only.
        </p>
      </div>

      {/* Document slots */}
      <div className="space-y-3">
        {REQUIRED_DOCS.map((req) => {
          const uploaded = docsQ.data?.items.filter((d) => d.document_type === req.type) ?? [];
          return (
            <DocSlot
              key={req.type}
              docType={req.type}
              label={req.label}
              hint={req.hint}
              required={req.required}
              uploaded={uploaded}
              uploading={uploadMut.isPending && uploadMut.variables?.docType === req.type}
              onUpload={(file) => uploadMut.mutate({ file, docType: req.type })}
              onDelete={(docId) => deleteMut.mutate(docId)}
            />
          );
        })}
      </div>

      {/* Upload error */}
      {uploadMut.isError && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
          Upload failed: {uploadMut.error?.message}
        </p>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between pt-2">
        <Link
          to={`/cases/${id}/wizard/criminal`}
          className="text-sm text-slate-500 hover:text-slate-900 transition-colors"
        >
          ← Background
        </Link>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(`/cases/${id}/wizard/oath`)}
            className="text-sm text-slate-500 hover:text-slate-900 transition-colors px-3 py-1.5"
          >
            Skip for now
          </button>
          <button
            type="button"
            onClick={() => navigate(`/cases/${id}/wizard/oath`)}
            className="rounded-xl bg-slate-900 text-white px-5 py-2.5 text-sm font-semibold hover:bg-slate-800 hover:-translate-y-0.5 hover:shadow-md transition-all duration-200"
          >
            Continue to Oath →
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// DocSlot — one document type row
// ---------------------------------------------------------------------------

function DocSlot({
  label,
  hint,
  required,
  uploaded,
  uploading,
  onUpload,
  onDelete,
}: {
  // docType is passed by the caller for symmetry with the upload
  // mutation but the slot itself doesn't need it — the label/hint
  // are pre-resolved upstream from the registry.
  docType: string;
  label: string;
  hint: string;
  required: boolean;
  uploaded: DocumentResponse[];
  uploading: boolean;
  onUpload: (file: File) => void;
  onDelete: (docId: string) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const hasUploads = uploaded.length > 0;

  const handleFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    Array.from(files).forEach((f) => onUpload(f));
  };

  return (
    <div className={cn(
      "rounded-2xl border bg-white shadow-sm overflow-hidden transition-all",
      hasUploads ? "border-emerald-200" : "border-slate-200/80",
    )}>
      {/* Header row */}
      <div className="flex items-start gap-3 px-5 pt-4 pb-3">
        {/* Status dot */}
        <div className={cn(
          "w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5 transition-colors",
          hasUploads ? "bg-emerald-500" : required ? "bg-slate-200" : "bg-slate-100",
        )}>
          {hasUploads && (
            <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-slate-900">{label}</span>
            {required && !hasUploads && (
              <span className="text-[10px] font-semibold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full">
                Recommended
              </span>
            )}
          </div>
          <p className="text-xs text-slate-400 mt-0.5">{hint}</p>
        </div>

        {/* Upload trigger */}
        <button
          type="button"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
          className="shrink-0 text-xs font-semibold text-blue-600 hover:text-blue-700 px-3 py-1.5 rounded-lg hover:bg-blue-50 transition-colors disabled:opacity-50"
        >
          {uploading ? "Uploading…" : hasUploads ? "+ Add more" : "Upload"}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept=".pdf,.jpg,.jpeg,.png"
          multiple
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
        />
      </div>

      {/* Drag-and-drop zone (shown when no uploads yet) */}
      {!hasUploads && !uploading && (
        <div
          className={cn(
            "mx-5 mb-4 border-2 border-dashed rounded-xl px-4 py-5 text-center cursor-pointer transition-colors",
            isDragging ? "border-blue-400 bg-blue-50" : "border-slate-200 hover:border-slate-300 hover:bg-slate-50",
          )}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={(e) => { e.preventDefault(); setIsDragging(false); handleFiles(e.dataTransfer.files); }}
          onClick={() => inputRef.current?.click()}
        >
          <svg className="w-6 h-6 text-slate-300 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
          </svg>
          <p className="text-xs text-slate-400">
            <span className="font-medium text-blue-600">Click to upload</span> or drag and drop
          </p>
          <p className="text-[11px] text-slate-300 mt-0.5">PDF, JPG, PNG</p>
        </div>
      )}

      {/* Uploaded files list */}
      {uploaded.length > 0 && (
        <div className="px-5 pb-4 space-y-2">
          {uploaded.map((doc) => (
            <div key={doc.id} className="flex items-center gap-3 bg-emerald-50 rounded-xl px-3 py-2.5">
              <FileIcon mimeType={doc.mime_type} />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-slate-700 truncate">
                  {doc.file_name_original ?? "document"}
                </p>
                <p className="text-[11px] text-slate-400">
                  {doc.size_bytes ? formatBytes(doc.size_bytes) : ""}{" "}
                  · {doc.processing_state}
                </p>
              </div>
              <button
                type="button"
                onClick={() => onDelete(doc.id)}
                className="shrink-0 p-1 text-slate-300 hover:text-red-500 transition-colors rounded-lg hover:bg-red-50"
                aria-label="Remove"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function FileIcon({ mimeType }: { mimeType?: string | null }) {
  const isPdf = mimeType === "application/pdf";
  return (
    <div className={cn(
      "w-7 h-7 rounded-lg flex items-center justify-center shrink-0",
      isPdf ? "bg-red-100" : "bg-blue-100",
    )}>
      <svg className={cn("w-3.5 h-3.5", isPdf ? "text-red-600" : "text-blue-600")} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    </div>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
